import React, { useEffect } from 'react'
import styled from 'styled-components'

import Theme from '../../styles/theme'
import { mediaMax } from '../../styles/media'

import Accordion from '../../components/display/Accordion'
import ButtonLink from '../../components/display/ButtonLink'
import Container from '../../components/layout/Container'
import GiftDetailedIcon from '../../components/icons/GiftDetailed'
import LazyImage from '../../components/display/LazyImage'
import MetaTags from '../../components/meta/MetaTags'
import UniformOverlay from '../../components/display/Overlay/Uniform'
import { Section, SectionHeader } from '../../components/display/Landing'

import Steps from './Steps'

const RedeemSection = styled(Section)`
  p {
    padding: 0 25px 0 25px;
  }
`

const Description = styled.div`
  h2 {
    font-size: 40px;
    color: ${({ theme }) => theme.brand.forest};

    ${mediaMax.md`
      font-size: 32px;
    `}
  }

  p {
    font-size: 20px;

    ${mediaMax.md`
      font-size: 16px;
    `}
  }

  & > * {
    margin-bottom: 24px;
  }

  margin-top: 20px;
  margin-bottom: 100px;
`

const IntroSection = styled.div`
  padding: 100px 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  color: ${({ theme }) => theme.brand.jungle};
  background-color: ${({ theme }) => theme.brand.sand};

  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 50px;
  }

  h3 {
    font-size: 32px;
    line-height: 42px;
    margin: 0;
    text-align: center;

    ${mediaMax.md`
      font-size: 20px;
      line-height: 1.8;
    `}
  }

  p {
    font-size: 20px;
    line-height: 36px;

    ${mediaMax.md`
      font-size: 16px;
      line-height: 1.8;
    `}
  }

  & > p {
    max-width: 860px;
    text-align: center;

    &:not(:last-child) {
      margin-bottom: 24px;
    }
  }

  & > * {
    max-width: 768px;
  }
`

const accordionItems = [
  {
    title: 'How will I receive my gift voucher?',
    content: `We'll email you the voucher as a PDF file for you
    to either print out or email to the recipient.`,
  },
  {
    title: 'How long are Riparide gift vouchers valid for?',
    content: `Our gift vouchers are valid for 3 years from the date of purchase.
    The expiry date is printed on the gift voucher.`,
  },
  {
    title: 'Do all listings on your site accept gift vouchers?',
    content: `They sure do! You can use a gift voucher to make a booking for any listing or story on our site.`,
  },
  {
    title: 'Can I buy a voucher for a specific escape?',
    content:
      'You sure can! On every listing and story page, you’ll see a “GIFT THIS” button, which will allow you to buy a voucher linked to that particular escape. And the best part is if that escape isn’t available when the recipient goes to book, they can still use the voucher for any other escape on our site. Best of both worlds!',
  },
  {
    title: 'Where can I find more information?',
    content: (
      <>
        You can view the Gift Voucher Terms and Conditions{' '}
        <a href="/terms" target="_blank">
          here
        </a>
        .
      </>
    ),
  },
]

const Title = styled.h1`
  font-size: 50px;
  letter-spacing: 0.25px;
  font-weight: 500;
  z-index: 1;
`

const Header = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  height: calc(75vh - 100px);
  min-height: 270px;
  padding: 80px 0;

  color: white;
  text-align: center;

  background-color: ${({ theme }) => theme['-ds-core-300'].value};

  ${LazyImage} {
    position: absolute;
    object-position: bottom center;
  }

  ${ButtonLink} {
    width: fit-content;
    margin: 0 auto;
    margin-top: 70px;
  }
`

const VoucherLanding = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Header>
        <UniformOverlay color={Theme['-ds-core-300'].value} zIndex={1} />
        <LazyImage
          data-src="https://assets.riparide.com/landing_pages/vouchers/header/header_v1.jpg"
          preload
        />
        <div style={{ zIndex: 2 }}>
          <Title>The Gift of Adventure</Title>
          <h2>Buy or redeem a gift voucher for a soul-fulfilling escape</h2>
          <ButtonLink href="/vouchers/create" rounded white>
            Buy a Riparide Voucher
          </ButtonLink>
        </div>
      </Header>
      <div className="qa-vouchers-landing">
        <IntroSection>
          <GiftDetailedIcon color={Theme.brand.jungle} />
          <h3>
            A Riparide gift voucher is the perfect gift for anyone who&apos;s on
            the run from an ordinary life.
          </h3>
          <p>
            Whether it&apos;s for a special occasion or to reward an employee,
            we could all do with a little push to get outdoors and do something
            to get our feet dirty, hair messy and soul sparkling.
          </p>
        </IntroSection>

        <Container>
          <Section marginBottom centered borderBottom>
            <Description>
              <h2>Give the Gift of Adventure</h2>
              <p>
                A Riparide Voucher is the ultimate &lsquo;choose your own
                adventure&rsquo;. Our vouchers can be used to book any Riparide
                escape and they&apos;re valid for three years from the date of
                purchase.
              </p>
            </Description>

            <Steps />

            <ButtonLink style={{ marginBottom: 50 }} href="/vouchers/create">
              Buy a Riparide Voucher
            </ButtonLink>
          </Section>

          <RedeemSection grey centered marginBottom>
            <SectionHeader>I want to redeem my gift voucher</SectionHeader>
            <p>
              Have you received a Riparide Voucher? Use it to book a
              soul-fulfilling escape.
            </p>
            <ButtonLink ghost href="/redeem">
              REDEEM VOUCHER
            </ButtonLink>
          </RedeemSection>

          <Section borderTop>
            <div className="row">
              <div className="col-xs-12 col-md-6">
                <SectionHeader>
                  Some common <br /> questions
                </SectionHeader>
              </div>
              <div className="col-xs-12 col-md-6">
                <Accordion items={accordionItems} />
              </div>
            </div>
          </Section>
        </Container>
      </div>
    </>
  )
}

export default VoucherLanding
